// Models
import {Beat, sameJudgement} from "./model"

/**
 * This browser's backup of the corrections.
 *
 * The server (PUT record/annotation/{ecgId}) is where corrections live. Every
 * save is written here first, so a dropped connection, a reload or a closed
 * tab cannot take the work with it; once the server has it the copy is marked
 * synced, and an unsynced copy wins over the server's on the next open.
 *
 * Per-browser and per-device: clearing site data loses it, and nobody else
 * sees it. It is a crash mat, not the store.
 */

const PREFIX = "annotation.beats.";
const INDEX = "annotation.records";

/** Beats plus the little that is needed to know what they belong to. */
export type Saved = {
    recordName: string
    sampleRate: number
    beats: Beat[]
    savedAt: string
    /** How many beats the record has — a reader can check the set is complete. */
    total?: number
    /**
     * True once the server has these exact beats. An unsynced copy is newer
     * than the server's and wins on the next open; a synced one does not.
     */
    synced?: boolean
    /**
     * When the server last changed each beat (its editedAt), as of the server
     * labels these edits started from — by beat number. Lets an unsynced copy
     * tell whether the server moved on since, so it never overwrites newer
     * work. Absent on copies written before this existed.
     */
    base?: {[beatNo: number]: string | null}
}

/** When the server last changed each beat, as the base for a copy. */
export function baseOf(serverBeats: Beat[]): {[beatNo: number]: string | null} {
    const base: {[beatNo: number]: string | null} = {};

    serverBeats.forEach(beat => {
        base[beat.number] = beat.reviewedAt
    });

    return base
}

function safeGet(key: string): string | null {
    try {
        return window.localStorage.getItem(key)
    } catch (error) {
        /* private mode, blocked storage, quota — treat as empty */
        return null
    }
}

function safeSet(key: string, value: string): boolean {
    try {
        window.localStorage.setItem(key, value);

        return true
    } catch (error) {
        return false
    }
}

function recordIds(): string[] {
    const raw = safeGet(INDEX);

    if (!raw) {
        return []
    }

    try {
        const parsed = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed : []
    } catch (error) {
        return []
    }
}

function rememberId(id: string) {
    const ids = recordIds();

    if (ids.indexOf(id) === -1) {
        safeSet(INDEX, JSON.stringify(ids.concat(id)))
    }
}

/**
 * Store every beat, not just the edited ones.
 *
 * Keeping only the touched beats was considered, to stay under the browser's
 * ~5 MB — but measured, a beat is ~250 bytes, so the whole CSE set of 125
 * ten-second records is about 370 KB. There is no quota problem to solve here,
 * and a full copy is what the next open merges over the server's labels.
 * (Holter-length records WOULD blow the budget at ~21 MB;
 * if this is ever pointed at those, the answer is to store sparsely and merge
 * on load, which `merge` below already does.)
 *
 * What was costing something was the write RATE — once per keystroke. The
 * caller debounces instead.
 *
 * @param id the record key
 * @param saved
 * @returns false when storage refused it — the caller should say so rather
 *     than let the technician believe the work is safe
 */
export function save(id: string, saved: Saved): boolean {
    const ok = safeSet(PREFIX + id, JSON.stringify(
        Object.assign({}, saved, {total: saved.beats.length}),
    ));

    if (ok) {
        rememberId(id)
    }

    return ok
}

/**
 * @param id
 */
export function load(id: string): Saved | null {
    const raw = safeGet(PREFIX + id);

    if (!raw) {
        return null
    }

    try {
        const parsed = JSON.parse(raw) as Saved;

        return parsed && Array.isArray(parsed.beats) ? parsed : null
    } catch (error) {
        return null
    }
}

/**
 * Lay an unsynced copy's corrections back over the server's labels.
 *
 * Matched by beat NUMBER, never by position, so a stored set that is sparse —
 * or a server that returns a different count — cannot silently shift every
 * mark onto the wrong beat.
 *
 * A stored beat is applied only if the server's beat has not changed since
 * the copy was based on it (same editedAt). Otherwise someone saved newer
 * work there — another technician, or this one on another device — and an
 * old copy must not overwrite it: the server's beat is kept, and counted as
 * a conflict for the screen to report. A copy from before bases were kept
 * applies only over beats nobody has edited on the server.
 *
 * @param fresh the server's labels
 * @param saved the unsynced copy, or null
 */
export function merge(fresh: Beat[], saved: Saved | null): {beats: Beat[], conflicts: number} {
    if (!saved || !saved.beats.length) {
        return {beats: fresh, conflicts: 0}
    }

    if (!fresh.length) {
        return {beats: saved.beats, conflicts: 0}
    }

    const byNumber: {[number: number]: Beat} = {};

    saved.beats.forEach(beat => {
        byNumber[beat.number] = beat
    });

    let conflicts = 0;
    let applied = 0;

    const beats = fresh.map(server => {
        const mine = byNumber[server.number];

        if (!mine || sameJudgement(mine, server)) {
            return server
        }

        const base = saved.base ? saved.base[server.number] : undefined;
        const unchanged = base === undefined ? server.reviewedAt === null : server.reviewedAt === base;

        if (unchanged) {
            applied += 1;

            return mine
        }

        conflicts += 1;

        return server
    });

    /* nothing of the copy applied: hand back the server's own array, so the
       screen does not differ from what the server has and nothing is saved */
    return {beats: applied ? beats : fresh, conflicts: conflicts}
}

export function clear(id: string) {
    try {
        window.localStorage.removeItem(PREFIX + id);
        safeSet(INDEX, JSON.stringify(recordIds().filter(x => x !== id)))
    } catch (error) {
        /* nothing useful to do — the server holds the work, not this */
    }
}
